import {
  all,
  call,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';

import { products as productService } from 'services/productService';
import purchasedComponentsService from 'services/purchasedComponentsService';

import { getGroupId } from 'store/session/selectors';

import { MODES } from '../constants';
import {
  componentDelete,
  open,
  purchasedComponentUpdateCreateModal,
  save,
  types,
} from './actions';
import { getMode, getPurchasedComponentId } from './reducer';

export default function* root() {
  yield takeLatest(types.OPEN_REQUEST, watchInitiateDrawer);
  yield takeLatest(types.SAVE_REQUEST, watchSave);
  yield takeLatest(types.DELETE_REQUEST, watchDelete);
}

function* watchInitiateDrawer(action) {
  try {
    const { payload } = action;
    const {
      deleteCallBack,
      mode,
      productId,
      purchasedComponentId,
      saveCallBack,
    } = payload;

    const groupId = yield select(getGroupId);
    if (mode === MODES.UPDATE) {
      const [purchasedComponent, customColumns] = yield all([
        call(purchasedComponentsService.get, purchasedComponentId),
        call(purchasedComponentsService.customColumns.list, groupId),
      ]);
      yield all([
        put(
          purchasedComponentUpdateCreateModal.setPurchasedComponent({
            purchasedComponent,
          }),
        ),
        put(
          purchasedComponentUpdateCreateModal.setCustomColumns({
            customColumns,
          }),
        ),
      ]);
    } else {
      const customColumns = yield call(
        purchasedComponentsService.customColumns.list,
        groupId,
      );
      yield put(
        purchasedComponentUpdateCreateModal.setCustomColumns({ customColumns }),
      );

      if (productId) {
        const product = yield call(productService.get, productId);
        yield put(purchasedComponentUpdateCreateModal.setProduct({ product }));
      }
    }

    yield put(open.success());

    // enact listener for custom callbacks if present
    if (saveCallBack || deleteCallBack) {
      const raceObject = {
        close: take(types.CLOSE),
      };
      if (saveCallBack) {
        raceObject.save = saveCallBack ? take(types.SAVE_SUCCESS) : undefined;
      }
      if (deleteCallBack) {
        raceObject.componentDelete = deleteCallBack
          ? take(types.DELETE_SUCCESS)
          : undefined;
      }
      const winner = yield race(raceObject);
      if (winner.save) {
        saveCallBack({
          purchasedComponent: winner.save.payload.purchasedComponent,
        });
      } else if (winner.componentDelete) {
        deleteCallBack({
          purchasedComponentId:
            winner.componentDelete.payload.purchasedComponentId,
        });
      }
    }
  } catch (error) {
    yield put(open.error(error));
  }
}

function* watchSave(action) {
  const { payload } = action;
  try {
    const mode = yield select(getMode);
    let purchasedComponent;
    if (mode === MODES.CREATE) {
      const groupId = yield select(getGroupId);
      const response = yield call(
        purchasedComponentsService.create,
        groupId,
        payload,
      );
      purchasedComponent = response;
    } else {
      const purchasedComponentId = yield select(getPurchasedComponentId);
      const response = yield call(
        purchasedComponentsService.update,
        purchasedComponentId,
        payload,
      );
      purchasedComponent = response;
    }

    yield put(save.success({ purchasedComponent }));
  } catch (error) {
    yield put(save.error(error));
  }
}

function* watchDelete() {
  const purchasedComponentId = yield select(getPurchasedComponentId);

  try {
    yield call(purchasedComponentsService.delete, purchasedComponentId);
    yield put(componentDelete.success({ purchasedComponentId }));
  } catch (error) {
    yield put(componentDelete.error(error));
  }
}
